import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from, map, shareReplay, switchMap } from 'rxjs';
import { PDFNotificationService, PDFScriptLoaderService } from 'ngx-extended-pdf-viewer';
import { resolvePdfViewerApplication } from '../utils/pdfjs-cleanup.utils';
import { ApiService } from './api.service';
import { TesseractEngineService, DetectionCropSession } from './tesseract-engine.service';
import {
    DocumentTypeIdentifierValue, DocumentTypeMatchResult, ReturnDocumentTypeIdentifiersResponse,
} from '../shared/components/signature-file-upload/file-upload.model';
import { ocrIdentifierValuesMatch } from '../utils/ocr-identifier-compare';

type Rect = { x1: number; y1: number; x2: number; y2: number };
/** One document type ready for OCR matching (empty regions = skip). */
type Candidate = {
    document_type_id: number;
    keys: string[];
    regions: { pageNumber: number; rect: Rect; expectedValue: string }[];
};

/**
 * Auto-picks document type by OCR-matching configured identifier boxes on the open PDF.
 * First type whose every zone matches wins. Runs in parallel OCR lanes (3, or 4 if many types).
 */
@Injectable({ providedIn: 'root' })
export class DocumentTypeDetectionService {
    private detectingSubject = new BehaviorSubject<boolean>(false);
    readonly isDetecting$ = this.detectingSubject.asObservable();
    /** Serializes detection runs so two PDFs never OCR at once. */
    private chain: Promise<unknown> = Promise.resolve();
    private refresh$ = new BehaviorSubject<void>(undefined);
    /** Cached API identifier configs; refresh via invalidateIdentifierCache(). */
    private identifiers$ = this.refresh$.pipe(
        switchMap(() => this.apiService.returnDocumentTypeIdentifiers()), shareReplay(1),
    );

    private readonly pdfScriptLoader = inject(PDFScriptLoaderService);
    private readonly pdfNotification = inject(PDFNotificationService);

    constructor(
        private apiService: ApiService,
        private tesseract: TesseractEngineService,
    ) {}

    /** Drop cached identifier configs so the next detect fetches fresh API data. */
    invalidateIdentifierCache(): void { this.refresh$.next(undefined); }

    /** Stream of document-type identifier configs (shared replay). */
    getDocumentTypeIdentifiers(): Observable<ReturnDocumentTypeIdentifiersResponse> { return this.identifiers$; }

    /** Public entry: load identifiers, enqueue one detect, emit match or null. */
    determineDocumentType(): Observable<DocumentTypeMatchResult | null> {
        return this.identifiers$.pipe(
            switchMap((items) => from(this.enqueue(() => this.detect(items)))), map((r) => r ?? null),
        );
    }

    /** Chain promises so only one detect runs at a time. */
    private enqueue<T>(fn: () => Promise<T>): Promise<T> {
        const run = this.chain.then(fn, fn);
        this.chain = run.then(() => undefined, () => undefined);
        return run;
    }

    /** ms → seconds (1 decimal) for readable TOTAL logs. */
    private sec(ms: number): number { return Math.round(ms / 100) / 10; }

    /**
     * Full detect pipeline: build candidates → parallel lane match → log timing → return winner.
     * Lanes: 3 by default, 4 when typesWithZones > 40.
     */
    private async detect(items: ReturnDocumentTypeIdentifiersResponse): Promise<DocumentTypeMatchResult | null> {
        this.detectingSubject.next(true);
        const t0 = Date.now();
        try {
            if (!items?.length) return null;
            const pdfApp = resolvePdfViewerApplication({
                loaderApp: this.pdfScriptLoader?.PDFViewerApplication,
                notificationApp: this.pdfNotification?.onPDFJSInitSignal?.(),
            });
            const pages =
                pdfApp?.pdfDocument?.numPages ??
                pdfApp?.pdfViewer?.pagesCount ??
                pdfApp?.pagesCount;
            if (typeof pages !== 'number' || pages < 1) {
                console.log('[DocTypeDetection] TOTAL aborted — no PDF');
                return null;
            }
            const pdfPages = Math.floor(pages);
            const candidates = this.buildCandidates(items, pdfPages);
            const withZones = candidates.filter((c) => c.regions.length > 0);
            if (!withZones.length) {
                console.log('[DocTypeDetection] TOTAL aborted — no OCR regions');
                return null;
            }

            const lanesWanted = withZones.length > 40 ? 4 : 3;
            const zonesEligible = withZones.reduce((n, c) => n + c.regions.length, 0);
            console.log(`[DocTypeDetection] start — typesWithZones ${withZones.length} → ${lanesWanted} lanes, zones ${zonesEligible}, apiTypes ${items.length}, pdfPages ${pdfPages}`);

            const m = await this.matchFirst(candidates, lanesWanted);
            const winner = m.winner;
            const s = (ms: number) => this.sec(ms);
            const totalSec = s(Date.now() - t0);
            console.log(
                `[DocTypeDetection] TOTAL ${totalSec}s | pool ${s(m.poolMs)}s openPdf ${s(m.openMs)}s match ${s(m.matchMs)}s`
                + ` | crop ${s(m.cropMs)}s ocr ${s(m.ocrMs)}s | lanes ${m.laneN}/${lanesWanted}`
                + ` poolWorkers ${this.tesseract.detectionWorkerCount} | zonesRan ${m.zones}`
                + (winner ? ` → type ${winner.document_type_id}` : ' → no match'),
            );
            m.laneStats.forEach((L) => console.log(
                `[DocTypeDetection] lane ${L.lane} — ${s(L.wallMs)}s (crop ${s(L.cropMs)}s, ocr ${s(L.ocrMs)}s)`
                + ` | typesTried ${L.typesTried}, zones ${L.zones}` + (L.won ? ' → winner' : ''),
            ));
            console.log('[DocTypeDetection] Summary —', {
                outcome: winner ? 'matched' : 'no_match', totalSec,
                poolSec: s(m.poolMs), openPdfSec: s(m.openMs), matchSec: s(m.matchMs),
                cropSec: s(m.cropMs), ocrSec: s(m.ocrMs), lanesWanted, lanesUsed: m.laneN,
                poolWorkers: this.tesseract.detectionWorkerCount, typesWithZones: withZones.length,
                zonesEligible, zonesRan: m.zones, apiTypes: items.length, pdfPages,
                matchedTypeId: winner?.document_type_id ?? null,
                candidateTypeIds: winner ? undefined : withZones.map((c) => c.document_type_id),
            });

            if (!winner) return null;
            const n = winner.regions.length;
            return {
                document_type_id: winner.document_type_id, matchCount: n, totalIdentifiers: n,
                matchedType: {
                    document_type_id: winner.document_type_id, identifierKeys: winner.keys,
                    zonesMatched: n, zonesRequiredForMatch: n,
                },
            };
        } catch (err) {
            console.log(`[DocTypeDetection] TOTAL ${this.sec(Date.now() - t0)}s → ERROR`, err);
            throw err;
        } finally {
            this.detectingSubject.next(false);
        }
    }

    /**
     * Split candidates across OCR lanes; each lane tries types until one fully matches.
     * Shared PDF crop session; first winner stops the other lanes.
     */
    private async matchFirst(candidates: Candidate[], lanesWanted: number) {
        const poolT0 = Date.now();
        await this.tesseract.awaitDetectionPool(lanesWanted);
        const poolMs = Date.now() - poolT0;
        const laneN = Math.min(lanesWanted, candidates.length, Math.max(1, this.tesseract.detectionWorkerCount));
        const lanes: Candidate[][] = Array.from({ length: laneN }, () => []);
        candidates.forEach((c, i) => lanes[i % laneN].push(c));

        const openT0 = Date.now();
        const session = await this.tesseract.openDetectionSession();
        const openMs = Date.now() - openT0;

        let winner: Candidate | null = null;
        let cropMs = 0, ocrMs = 0, zones = 0;
        const laneStats = Array.from({ length: laneN }, (_, lane) => ({
            lane, wallMs: 0, cropMs: 0, ocrMs: 0, zones: 0, typesTried: 0, won: false,
        }));
        const matchT0 = Date.now();
        try {
            await Promise.all(lanes.map(async (types, worker) => {
                const st = laneStats[worker];
                const laneT0 = Date.now();
                for (const type of types) {
                    if (winner || !type.regions.length) continue;
                    st.typesTried++;
                    if (await this.zonesMatch(type, worker, session, () => !!winner, (c, o) => {
                        cropMs += c; ocrMs += o; zones++;
                        st.cropMs += c; st.ocrMs += o; st.zones++;
                    })) {
                        winner = type; st.won = true; st.wallMs = Date.now() - laneT0; return;
                    }
                }
                st.wallMs = Date.now() - laneT0;
            }));
        } finally { session.destroy(); }
        return { winner, laneN, poolMs, openMs, matchMs: Date.now() - matchT0, cropMs, ocrMs, zones, laneStats };
    }

    /**
     * True only if every identifier zone for this type OCRs to the expected value.
     * Stops early if another lane already won (`stopped`) or one zone fails.
     */
    private async zonesMatch(
        type: Candidate, worker: number, session: DetectionCropSession,
        stopped: () => boolean, onZone: (cropMs: number, ocrMs: number) => void,
    ): Promise<boolean> {
        for (let i = 0; i < type.regions.length; i++) {
            if (stopped()) return false;
            const region = type.regions[i];
            const cropT0 = Date.now();
            let image: OffscreenCanvas | null = null;
            try { image = await session.renderCrop(region); } catch { image = null; }
            let text = '', ocrTook = 0;
            if (image && !stopped()) {
                const ocrT0 = Date.now();
                text = await this.tesseract.recognizeOnWorker(worker, image, {
                    pipeline: 'docTypeDetection', expectedValue: region.expectedValue,
                    document_type_id: type.document_type_id, zoneIndex: i,
                });
                ocrTook = Date.now() - ocrT0;
            }
            onZone(Date.now() - cropT0, ocrTook);
            if (!ocrIdentifierValuesMatch(text, region.expectedValue)) return false;
        }
        return true;
    }

    /**
     * Map API identifier configs → Candidates.
     * Types with out-of-range pages get empty regions (skipped). Boxes must sit on the page.
     */
    private buildCandidates(items: ReturnDocumentTypeIdentifiersResponse, pageCount: number): Candidate[] {
        const bad = (ivs: DocumentTypeIdentifierValue[]) =>
            ivs.some((iv) => { const p = iv.page ?? 1; return p < 1 || p > pageCount; });
        const needed = new Set<number>();
        for (const item of items) {
            const ivs = item.identifier_values ?? [];
            if (!bad(ivs)) ivs.forEach((iv) => needed.add(iv.page ?? 1));
        }
        const sizes = this.pageSizes(pageCount, needed);
        return items.map((item) => {
            const ivs = item.identifier_values ?? [];
            if (bad(ivs)) return { document_type_id: item.document_type_id, keys: [], regions: [] };
            return {
                document_type_id: item.document_type_id,
                keys: ivs.map((iv) => iv.key).filter((k): k is string => !!k),
                regions: ivs.filter((iv) => this.usableBox(iv, sizes.get(iv.page ?? 1))).map((iv) => ({
                    pageNumber: iv.page ?? 1,
                    rect: { x1: iv.x1, y1: iv.y1, x2: iv.x2, y2: iv.y2 },
                    expectedValue: iv.identifier_value,
                })),
            };
        });
    }

    /** Non-empty box; if page size known, must lie inside the page (small tolerance). */
    private usableBox(iv: DocumentTypeIdentifierValue, page?: { width: number; height: number }): boolean {
        const { x1, x2, y1, y2 } = iv;
        if (Math.abs(x2 - x1) < 1 || Math.abs(y2 - y1) < 1) return false;
        if (!page || ![x1, x2, y1, y2].every(Number.isFinite)) return !page;
        return Math.min(x1, x2) >= -0.01 && Math.max(x1, x2) <= page.width + 0.01
            && Math.min(y1, y2) >= -0.01 && Math.max(y1, y2) <= page.height + 0.01;
    }

    /**
     * Page width/height from the viewer page views only.
     * Never call pdfDocument.getPage() here — that races the UI renderer.
     */
    private pageSizes(pageCount: number, pages: Set<number>): Map<number, { width: number; height: number }> {
        const out = new Map<number, { width: number; height: number }>();
        const viewer = resolvePdfViewerApplication({
            loaderApp: this.pdfScriptLoader?.PDFViewerApplication,
            notificationApp: this.pdfNotification?.onPDFJSInitSignal?.(),
        })?.pdfViewer;
        if (!viewer?.getPageView) return out;
        for (const p of pages) {
            if (p < 1 || p > pageCount) continue;
            try {
                const vp = viewer.getPageView(p - 1)?.pdfPage?.getViewport?.({ scale: 1 });
                if (vp?.width > 0 && vp?.height > 0) out.set(p, { width: vp.width, height: vp.height });
            } catch { /* skip */ }
        }
        return out;
    }
}
